import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { QRCodeSVG } from 'qrcode.react';
import { notifySuccess, notifyError } from '../../Notification';
import './googleReviewSlideEdit.css';
import { fetchStores } from '../../../redux/storesSlice';
import GenericModal from '../../genericModal/GenericModal';
import '../slide1Edit/slide1Edit.css';

const GoogleReviewSlideEdit = ({
    selectedSlide,
    isNewSlide,
    totalSlides,
    deckId,
    updatedDeckName,
    slide1,
    viewDeck,
    saveEditedSlide
}) => {

  const { items: stores } = useSelector((state) => state.stores);

  // Fetching known sites from the Redux store
  const knownSites = stores.map(store => ({
    siteName: store.name,
    googleUrl: store.google_review_url
  }));


  const [siteName, setSiteName] = useState(selectedSlide?.site_name || '');
  const [googleUrl, setGoogleUrl] = useState(selectedSlide?.google_url || '');
  const [isNewSite, setIsNewSite] = useState(false);
  
  const [slideBackgroundColor, setSlideBackgroundColor] = useState(selectedSlide?.background_color || (slide1 ? slide1.background_color : '#fafafa'));
  const [initialState, setInitialState] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();

  // Effect 1: Sync state with props from the backend
  useEffect(() => {
    if (selectedSlide) {
      const state = {
        siteName: selectedSlide.site_name || '',
        googleUrl: selectedSlide.google_url || '',
        slideBackgroundColor: selectedSlide.background_color || (slide1 ? slide1.background_color : '#fafafa'),
      };
      setSiteName(state.siteName);
      setGoogleUrl(state.googleUrl);
      setSlideBackgroundColor(state.slideBackgroundColor);
      setInitialState(state);
      setHasChanges(false);
    }
  }, [selectedSlide, slide1]);

  // Effect 2: Compare current state with initial state to detect changes
  useEffect(() => {
    if (!initialState) return;

    const currentChanges =
      siteName !== initialState.siteName ||
      googleUrl !== initialState.googleUrl ||
      slideBackgroundColor !== initialState.slideBackgroundColor;

    setHasChanges(currentChanges);
  }, [siteName, googleUrl, slideBackgroundColor, initialState]);

  // get store_id from siteName in stores array
  const storeId = stores.find(store => store.name === siteName)?.store_id;

  const backgroundColorOptions = [
    { color: '#fafafa', name: 'Default Light' },
    { color: '#f0f9ff', name: 'Light Blue' },
    { color: '#f0fdf4', name: 'Light Green' },
    { color: '#fefce8', name: 'Light Yellow' },
    { color: '#fdf2f8', name: 'Light Pink' },
    { color: '#f3f4f6', name: 'Light Gray' },
    { color: '#fef9c3', name: 'Light Cream' },
    { color: '#e0f2fe', name: 'Light Cyan' },
    { color: '#ecfdf5', name: 'Light Mint' },
    { color: '#fff7ed', name: 'Light Peach' },
    { color: '#f5f3ff', name: 'Light Lavender' },
    { color: '#f9fafb', name: 'Light Silver' }
  ];

  useEffect(() => {
    const matchedSite = knownSites.find(site => site.siteName === siteName);
    if (matchedSite) {
      setGoogleUrl(matchedSite.googleUrl);
      setIsNewSite(false);
    } else {
      setGoogleUrl('');
      setIsNewSite(true);
    }
  }, [siteName, knownSites]);

   useEffect(() => {
      // Dynamically set CSS variables
      document.documentElement.style.setProperty('--google-review-slide-bg-color', slideBackgroundColor);
   
    }, [slideBackgroundColor]);


  const handleBackgroundColorChange = (color) => {
    setSlideBackgroundColor(color);
  };

  const handleSubmitSlide = async () => {
    setLoading(true);
    // Prepare slide data for submission
    const slideData = {
      deck_id: deckId,
      deck_name: updatedDeckName,
      site_name: siteName,
      google_review_url: googleUrl,
      background_color: slideBackgroundColor,
      email: localStorage.getItem('user'),
      slide_number: selectedSlide?.slide_number || (totalSlides + 1),
      type: 'google_review',
      slide_id: selectedSlide?.slide_id || `google-review-${Date.now()}`,
      store_id: storeId,
    };

    try {
      await saveEditedSlide(slideData);
      setLoading(false);
      setHasChanges(false); // Reset changes after successful save
    } catch (error) {
      setLoading(false);
      notifyError('Failed to save Google Review slide.');
      console.error('Error saving Google Review slide:', error);
    }
  };

  // fetch stores
  useEffect(() => {
    dispatch(fetchStores(localStorage.getItem('selectedBusinessId')));
  }, [dispatch]);

  return (
    <div className='slide1-container-updated'>
      <div className='slide1-sidebar'>
        <div className='slide1-section'>
         
          <div className="input-group">
            <label className='slide1-section-title'>Site Name:</label>
            <input
              type="text"
              value={siteName}
              onChange={(e) => setSiteName(e.target.value)}
              placeholder="Select your site"
              list="site-options"
              disabled={viewDeck}
            />
            <datalist id="site-options">
              {knownSites.map((site, index) => (
                <option key={index} value={site?.siteName} />
              ))}
            </datalist>
            {isNewSite && (
              <div className='google-review-slide-new-site-message'>
                Can't find your site? You can add a new one by pasting your Google Review link.
              </div>
            )}
          </div>

          <div className="input-group">
            <label className='slide1-section-title'>Google Review URL:</label>
            <input
              type="text"
              value={googleUrl}
              onChange={(e) => {
                setGoogleUrl(e.target.value);
                setIsNewSite(true);
              }}
              placeholder="Paste your Google review link"
              disabled={viewDeck || !isNewSite}
            />
          </div>
        </div>

        {/* Background Color Section - Updated with fixed options */}
        <div className='slide1-section'>
          <h3 className='slide1-section-title'>Background Color</h3>
          <div className='slide1-background-color-grid'>
            {backgroundColorOptions.map((option, index) => (
              <button
                key={index}
                onClick={() => handleBackgroundColorChange(option.color)}
                className={`slide1-background-color-option ${slideBackgroundColor === option.color ? 'google-review-slide-background-color-active' : ''}`}
                disabled={viewDeck}
                title={option.name}
              >
                <div 
                  className='slide1-background-color-preview' 
                  style={{ backgroundColor: option.color }}
                />
              </button>
            ))}
          </div>
        </div>

        <div className='slide1-action-buttons'>
          {!viewDeck && (
            <>
              <button 
                onClick={handleSubmitSlide} 
                className='slide1-btn slide1-btn-primary'
                disabled={loading || !hasChanges}
              >
                {loading ? 'Saving...' : (isNewSlide ? '📋 Add to Deck' : '📋 Update Deck')}
              </button>
              <button 
                onClick={() => setShowPreview(true)} 
                className='slide1-btn slide1-btn-secondary'
              >
                👁 Preview
              </button>
            </>
          )}
        </div>
      </div>

      <div className='google-review-slide-preview'>
        <div 
          className='google-review-slide-preview-content'
          style={{ backgroundColor: slideBackgroundColor }}
        >
          <div className="google-review-slide-sub-container">
            { googleUrl ? (
              <div className="qr-code-section">
                <p className="qr-title">Want to provide more FDBK?</p>
                <QRCodeSVG value={googleUrl} size={250} />
              </div>
            ) : (
              <div className="qr-code-placeholder">
                <p>No QR code yet — enter a valid site to generate one.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <GenericModal show={showPreview} onClose={() => setShowPreview(false)} size="lg"
          header=
            {
              <div className="custom-modal-header">
                <div className="modern-preview-icon">
                  <svg width="28" height="28" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M2.458 12C3.732 7.943 7.523 5 12 5s8.268 2.943 9.542 7c-1.274 4.057-5.065 7-9.542 7s-8.268-2.943-9.542-7z"
                    />
                  </svg>
                </div>

                <div className="deck-modal-header-content">
                  <h1 className='slide1-main-title'>Slide Preview</h1>
                </div>
              </div>
            }
       body={
          <div 
            className='slide1-preview-content'
            style={{ backgroundColor: slideBackgroundColor, height: '400px' }}
          >
            <div className="slide1-preview-title">
              { googleUrl ? (
                <div className="qr-code-section">
                  <p className="qr-title">Want to provide more FDBK?</p>
                  <QRCodeSVG value={googleUrl} size={250} />
                </div>
              ) : (
                <div className="qr-code-placeholder">
                  <p>No QR code yet — enter a valid site to generate one.</p>
                </div>
              )}
            </div>
          </div>
          
      }
      />
    </div>
  );
};

export default GoogleReviewSlideEdit;